import React, { useState, useEffect } from 'react';
import { Users, Copy, Share2, CheckCircle, ArrowLeft, Home } from 'lucide-react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useWallet } from '@solana/wallet-adapter-react';

// Get backend URL from environment
const API = process.env.REACT_APP_BACKEND_URL?.endsWith('/api') 
  ? process.env.REACT_APP_BACKEND_URL 
  : `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReferPage = () => {
  const { publicKey, connected } = useWallet();
  const [referralStats, setReferralStats] = useState({
    totalReferrals: 0,
    creditsEarned: 0,
    successfulReferrals: 0
  });
  const [referralCode, setReferralCode] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (connected && publicKey) {
      fetchReferralData();
    }
  }, [connected, publicKey]);

  const fetchReferralData = async () => {
    try {
      const response = await axios.get(`${API}/referral/stats/${publicKey.toString()}`);
      setReferralStats({
        totalReferrals: response.data.total_referrals || 0,
        creditsEarned: response.data.credits_earned || 0,
        successfulReferrals: response.data.successful_referrals || 0
      });
      setReferralCode(response.data.referral_code || '');
    } catch (error) {
      console.error('Failed to fetch referral data:', error);
    }
  };

  const copyReferralLink = () => {
    const referralLink = `${window.location.origin}?ref=${referralCode}`;
    navigator.clipboard.writeText(referralLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const shareReferralLink = () => {
    const referralLink = `${window.location.origin}?ref=${referralCode}`;
    if (navigator.share) {
      navigator.share({
        title: 'Join me on TokenOS',
        text: 'Check out TokenOS - AI-powered operating system for launching web3 projects!',
        url: referralLink
      }).catch(err => console.error('Error sharing:', err));
    } else {
      copyReferralLink();
    }
  };

  if (!connected) {
    return (
      <div className="min-h-screen bg-black text-white pt-16 flex items-center justify-center">
        <div className="text-center">
          <Users className="h-16 w-16 text-purple-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Connect Wallet to Access Refer & Earn Program</h2>
          <p className="text-gray-400">Connect your Solana wallet to view your referral stats and earn credits</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pt-16">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <Link
              to="/"
              className="flex items-center space-x-1 px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-sm transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Home</span>
            </Link>
            <h1 className="text-3xl font-bold">Refer & Earn Program</h1>
          </div>
          <p className="text-gray-300">
            Invite friends to TokenOS and earn credits for each successful referral!
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-medium">Total Referrals</h3>
              <Users className="h-5 w-5 text-purple-400" />
            </div>
            <p className="text-3xl font-bold text-white">{referralStats.totalReferrals}</p>
          </div>
          
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-medium">Credits Earned</h3>
              <CheckCircle className="h-5 w-5 text-green-400" />
            </div>
            <p className="text-3xl font-bold text-white">{referralStats.creditsEarned}</p>
          </div>
          
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-medium">Successful Referrals</h3>
              <CheckCircle className="h-5 w-5 text-blue-400" />
            </div>
            <p className="text-3xl font-bold text-white">{referralStats.successfulReferrals}</p>
          </div>
        </div>

        {/* Referral Link */}
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 mb-8">
          <h3 className="text-xl font-semibold mb-4">Your Referral Link</h3>
          <div className="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-4">
            <div className="flex-1 bg-gray-900 rounded-lg p-3 border border-gray-700 overflow-hidden">
              <p className="text-purple-400 truncate">
                {referralCode ? `${window.location.origin}?ref=${referralCode}` : 'Loading...'}
              </p>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={copyReferralLink}
                className="flex items-center space-x-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
              >
                {copied ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                <span>{copied ? 'Copied!' : 'Copy Link'}</span>
              </button>
              <button
                onClick={shareReferralLink}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                <Share2 className="h-4 w-4" />
                <span>Share Link</span>
              </button>
            </div>
          </div>
        </div>

        {/* How It Works */}
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <h3 className="text-xl font-semibold mb-4">How It Works</h3>
          <div className="space-y-4">
            <div className="flex items-start space-x-4">
              <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold">1</span>
              </div>
              <div>
                <h4 className="font-medium text-white">Share Your Referral Link</h4>
                <p className="text-gray-300">Share your unique referral link with friends, on social media, or in your community.</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4">
              <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold">2</span>
              </div>
              <div>
                <h4 className="font-medium text-white">Friends Join TokenOS</h4>
                <p className="text-gray-300">When someone clicks your link and connects their wallet, they're registered as your referral.</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4">
              <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold">3</span>
              </div>
              <div>
                <h4 className="font-medium text-white">Earn Credits</h4>
                <p className="text-gray-300">You earn 5 credits for each person who joins through your link.</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4">
              <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold">4</span>
              </div>
              <div>
                <h4 className="font-medium text-white">Bonus Credits</h4>
                <p className="text-gray-300">Earn an additional 20 credits when your referrals make their first purchase.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferPage;
